import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { ArrowSmallDownIcon, ArrowSmallUpIcon } from '@dynatrace/strato-icons';

import { Button, type ButtonOwnProps } from '../../../buttons/button/Button.js';
import { BASE_CODE_EDITOR_MESSAGES as messages } from '../baseCodeEditorMessages.js';
import { MAX_DISPLAYED_ERROR_COUNT } from './off-screen-diagnostic-indicators.utils.js';
import * as styles from './OffScreenDiagnosticIndicators.sty.js';

/**
 * @internal
 */
interface OffScreenDiagnosticIndicatorsProps {
  topErrorsCount: number;
  bottomErrorsCount: number;
  handleScrollToNearestAbove: (
    event: ReactMouseEvent<HTMLButtonElement>,
  ) => void;
  handleScrollToNearestBelow: (
    event: ReactMouseEvent<HTMLButtonElement>,
  ) => void;
}

/**
 * Delays unmount lifecycle of a pill button so the fade-out animation
 * can complete before the element is removed from DOM
 *
 * While `count` is positive the pill is visible and in its normal (fade-in)
 * state. When `count` drops to 0 the pill enters the "exiting" state, keeps
 * the last non-zero count for its label, and stays in the DOM until the
 * `animationend` event fires. When `count` becomes positive again mid-exit the
 * exit is cancelled and the pill transitions back to its normal state.
 */
function usePillExitAnimation(count: number): {
  displayedCount: number;
  isExiting: boolean;
  isVisible: boolean;
  handleAnimationEnd: () => void;
} {
  const [isExiting, setIsExiting] = useState(false);
  const [exitingCount, setExitingCount] = useState(0);
  const prevCountRef = useRef(count);

  useEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = count;

    if (count === 0 && prevCount > 0) {
      queueMicrotask(() => {
        setExitingCount(prevCount);
        setIsExiting(true);
      });
    } else if (count > 0 && isExiting) {
      queueMicrotask(() => setIsExiting(false));
    }
  }, [count, isExiting]);

  return {
    displayedCount: isExiting ? exitingCount : count,
    isExiting,
    isVisible: count > 0 || isExiting,
    handleAnimationEnd: () => {
      if (isExiting) {
        setIsExiting(false);
      }
    },
  };
}

function getPillLabel(count: number, intl: ReturnType<typeof useIntl>): string {
  if (count > MAX_DISPLAYED_ERROR_COUNT) {
    return intl.formatMessage(messages.offScreenErrorCountCapped);
  }
  return intl.formatMessage(messages.offScreenErrorCount, {
    count,
  });
}

const pillButtonConfig: Pick<ButtonOwnProps, 'variant' | 'color' | 'size'> = {
  variant: 'emphasized',
  color: 'critical',
  size: 'condensed',
};

/**
 * Renders floating pill buttons at the top and/or bottom edge of the editor
 * viewport, indicating how many error-severity diagnostics are scrolled out of
 * view in each direction. Clicking a pill scrolls the nearest off-screen error
 * into view.
 * @internal
 */
export function OffScreenDiagnosticIndicators(
  props: Readonly<OffScreenDiagnosticIndicatorsProps>,
) {
  const {
    topErrorsCount,
    bottomErrorsCount,
    handleScrollToNearestAbove,
    handleScrollToNearestBelow,
  } = props;

  const intl = useIntl();

  const topPill = usePillExitAnimation(topErrorsCount);
  const bottomPill = usePillExitAnimation(bottomErrorsCount);

  return (
    <>
      {topPill.isVisible && (
        <Button
          {...pillButtonConfig}
          className={styles.errorIndicatorPill({
            direction: 'top',
            exiting: topPill.isExiting,
          })}
          onClick={handleScrollToNearestAbove}
          onAnimationEnd={topPill.handleAnimationEnd}
          aria-label={intl.formatMessage(messages.offScreenErrorsAboveLabel, {
            count: topPill.displayedCount,
          })}
        >
          <Button.Prefix>
            <ArrowSmallUpIcon />
          </Button.Prefix>
          {getPillLabel(topPill.displayedCount, intl)}
        </Button>
      )}
      {bottomPill.isVisible && (
        <Button
          {...pillButtonConfig}
          className={styles.errorIndicatorPill({
            direction: 'bottom',
            exiting: bottomPill.isExiting,
          })}
          onClick={handleScrollToNearestBelow}
          onAnimationEnd={bottomPill.handleAnimationEnd}
          aria-label={intl.formatMessage(messages.offScreenErrorsBelowLabel, {
            count: bottomPill.displayedCount,
          })}
        >
          <Button.Prefix>
            <ArrowSmallDownIcon />
          </Button.Prefix>
          {getPillLabel(bottomPill.displayedCount, intl)}
        </Button>
      )}
    </>
  );
}
